import { EmbeddedSignupResponse, FacebookAuthResponse, WABADetails } from '../types/FireAgent';

// Declaração global do Facebook SDK
declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

class EmbeddedSignupService {
  private appId: string | null = null;
  private redirectUri: string = '';

  /**
   * Obtém o App ID da variável de ambiente
   */
  private getAppId(): string {
    const appId = process.env.REACT_APP_FACEBOOK_APP_ID;
    if (!appId) {
      throw new Error('REACT_APP_FACEBOOK_APP_ID não configurado. Configure a variável de ambiente.');
    }
    return appId;
  }

  /**
   * Inicializa o Facebook SDK dinamicamente
   */
  async initializeFacebookSDK(): Promise<void> {
    this.appId = this.getAppId();
    
    // Determinar redirect URI baseado no ambiente
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    this.redirectUri = isDevelopment
      ? `${window.location.origin}/integracao/embedded-signup/callback`
      : `https://painel.swimmingpool.com.br/integracao/embedded-signup/callback`;

    return new Promise((resolve, reject) => {
      // Verificar se o SDK já foi carregado
      if (window.FB) {
        this.initFB();
        resolve();
        return;
      }

      // Carregar o SDK dinamicamente
      window.fbAsyncInit = () => {
        this.initFB();
        resolve();
      };

      const script = document.createElement('script');
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://connect.facebook.net/pt_BR/sdk.js';
      script.onerror = () => reject(new Error('Erro ao carregar Facebook SDK'));
      document.body.appendChild(script);
    });
  }

  private initFB(): void {
    if (!this.appId) {
      throw new Error('App ID não configurado');
    }

    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    window.FB.init({
      appId: this.appId,
      cookie: true,
      xfbml: true,
      version: 'v24.0',
      // Em desenvolvimento, permitir HTTP (localhost)
      // Em produção, o Facebook exige HTTPS
      ...(isDevelopment && {
        // Para desenvolvimento local, o app precisa estar em modo de desenvolvimento no Facebook
        // e localhost precisa estar adicionado nas configurações do app
      })
    });
  }

  /**
   * Faz login com Facebook e solicita permissões necessárias
   */
  async loginWithFacebook(): Promise<FacebookAuthResponse> {
    if (!window.FB) {
      throw new Error('Facebook SDK não inicializado. Chame initializeFacebookSDK primeiro.');
    }

    return new Promise((resolve, reject) => {
      window.FB.login(
        (response: any) => {
          if (response.authResponse) {
            resolve({
              accessToken: response.authResponse.accessToken,
              expiresIn: response.authResponse.expiresIn || 0,
              signedRequest: response.authResponse.signedRequest,
              userID: response.authResponse.userID
            });
          } else {
            reject(new Error('Usuário cancelou o login ou não autorizou as permissões'));
          }
        },
        {
          scope: 'business_management,whatsapp_business_management,whatsapp_business_messaging',
          return_scopes: true
        }
      );
    });
  }

  /**
   * Inicia o fluxo de Embedded Signup
   */
  startEmbeddedSignup(accessToken: string): void {
    if (!this.appId) {
      this.appId = this.getAppId();
    }

    // URL do Embedded Signup da Meta
    const embeddedSignupUrl = `https://www.facebook.com/v24.0/dialog/oauth?client_id=${this.appId}&redirect_uri=${encodeURIComponent(this.redirectUri)}&scope=business_management,whatsapp_business_management,whatsapp_business_messaging&response_type=code&state=${this.generateState()}`;
    
    // Abrir em popup ou redirecionar
    window.location.href = embeddedSignupUrl;
  }

  /**
   * Gera um state parameter para segurança OAuth
   */
  private generateState(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Processa o callback OAuth e troca code por access token
   */
  async handleOAuthCallback(code: string, appSecret?: string): Promise<EmbeddedSignupResponse> {
    if (!appSecret) {
      // Se não tiver App Secret, tentar usar o token do Facebook Login
      // Nota: Isso pode não funcionar para todos os casos. Idealmente, o App Secret deve estar no backend.
      throw new Error('App Secret é necessário para trocar code por token. Esta operação deve ser feita no backend por segurança.');
    }

    const appId = this.getAppId();
    
    // Trocar code por access token
    const accessToken = await this.exchangeCodeForToken(code, appId, appSecret);
    
    // Buscar detalhes da WABA
    const wabaDetails = await this.fetchWABADetails(accessToken);
    
    return {
      business_id: wabaDetails.business_id,
      waba_id: wabaDetails.waba_id,
      phone_number_id: wabaDetails.phone_number_id,
      access_token: accessToken,
      expires_in: 5184000, // 60 dias (padrão Meta)
      token_type: 'Bearer',
      configuradoEm: new Date().toISOString(),
      configuradoPor: 'Embedded Signup'
    };
  }

  /**
   * Troca o código de autorização por access token
   * NOTA: Esta função requer App Secret, que não deve ser exposto no frontend.
   * Idealmente, isso deve ser feito no backend.
   */
  private async exchangeCodeForToken(code: string, appId: string, appSecret: string): Promise<string> {
    const tokenUrl = `https://graph.facebook.com/v24.0/oauth/access_token?client_id=${appId}&client_secret=${appSecret}&redirect_uri=${encodeURIComponent(this.redirectUri)}&code=${code}`;
    
    const response = await fetch(tokenUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Erro ao trocar code por token');
    }

    const data = await response.json();
    return data.access_token;
  }

  /**
   * Busca detalhes da WABA (business_id, waba_id, phone_number_id)
   */
  async fetchWABADetails(accessToken: string): Promise<WABADetails> {
    try {
      // Primeiro, buscar os Business Managers associados
      const businessUrl = `https://graph.facebook.com/v24.0/me/businesses?access_token=${accessToken}`;
      const businessResponse = await fetch(businessUrl);
      
      if (!businessResponse.ok) {
        throw new Error('Erro ao buscar Business Managers');
      }

      const businessData = await businessResponse.json();
      
      if (!businessData.data || businessData.data.length === 0) {
        throw new Error('Nenhum Business Manager encontrado');
      }

      const businessId = businessData.data[0].id;

      // Buscar WABAs associados ao Business Manager
      const wabaUrl = `https://graph.facebook.com/v24.0/${businessId}/owned_whatsapp_business_accounts?access_token=${accessToken}`;
      const wabaResponse = await fetch(wabaUrl);
      
      if (!wabaResponse.ok) {
        throw new Error('Erro ao buscar WhatsApp Business Accounts');
      }

      const wabaData = await wabaResponse.json();
      
      if (!wabaData.data || wabaData.data.length === 0) {
        throw new Error('Nenhum WhatsApp Business Account encontrado');
      }

      const wabaId = wabaData.data[0].id;

      // Buscar números de telefone associados à WABA
      const phoneUrl = `https://graph.facebook.com/v24.0/${wabaId}/phone_numbers?access_token=${accessToken}`;
      const phoneResponse = await fetch(phoneUrl);
      
      if (!phoneResponse.ok) {
        throw new Error('Erro ao buscar números de telefone');
      }

      const phoneData = await phoneResponse.json();
      
      if (!phoneData.data || phoneData.data.length === 0) {
        throw new Error('Nenhum número de telefone encontrado');
      }

      const phoneNumberId = phoneData.data[0].id;

      return {
        business_id: businessId,
        waba_id: wabaId,
        phone_number_id: phoneNumberId
      };
    } catch (error: any) {
      console.error('Erro ao buscar detalhes da WABA:', error);
      throw new Error(error.message || 'Erro ao buscar detalhes da WABA');
    }
  }

  /**
   * Verifica se o usuário está logado no Facebook
   */
  async getLoginStatus(): Promise<{ status: string; authResponse?: any }> {
    if (!window.FB) {
      throw new Error('Facebook SDK não inicializado');
    }

    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: any) => {
        resolve(response);
      });
    });
  }

  /**
   * Faz logout do Facebook
   */
  async logout(): Promise<void> {
    if (!window.FB) {
      return;
    }

    return new Promise((resolve) => {
      window.FB.logout(() => {
        resolve();
      });
    });
  }
}

export const embeddedSignupService = new EmbeddedSignupService();

